import { useState, useEffect, useRef, useCallback } from "react";
import {
  getPrimaryAssets,
  getAllAssets,
  PRELOAD_CONFIG,
} from "../config/assetManifest";

/**
 * Preloads an image and returns a promise
 */
const preloadImage = (src) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      console.log(`✅ [Image] Loaded: ${src}`);
      resolve({ src, success: true });
    };
    img.onerror = () => {
      console.warn(`❌ [Image] Failed: ${src}`);
      resolve({ src, success: false });
    };
    img.src = src;
  });
};

/**
 * Preloads a video using fetch (caches the resource)
 */
const preloadVideo = (src) => {
  console.log(`🎬 [Video] Fetching: ${src}`);
  return fetch(src, { mode: "cors" })
    .then(() => {
      console.log(`✅ [Video] Loaded: ${src}`);
      return { src, success: true };
    })
    .catch(() => {
      console.warn(`❌ [Video] Failed: ${src}`);
      return { src, success: false };
    });
};

/**
 * Determines if a URL is a video based on extension
 */
const isVideo = (src) => {
  const videoExts = [".mp4", ".webm", ".ogg", ".mov"];
  return videoExts.some((ext) => src.toLowerCase().endsWith(ext));
};

/**
 * Preloads a single asset (image or video)
 */
const preloadAsset = (src) => {
  return isVideo(src) ? preloadVideo(src) : preloadImage(src);
};

/**
 * Custom hook for asset preloading with smart timeout
 *
 * @returns {Object} { isReady, progress, loadedCount, totalCount }
 */
export const useAssetPreloader = () => {
  const [isReady, setIsReady] = useState(false);
  const [loadedCount, setLoadedCount] = useState(0);
  const primaryAssets = useRef(getPrimaryAssets());
  const allAssets = useRef(getAllAssets());
  const totalCount = primaryAssets.current.length;
  const hasStartedBackground = useRef(false);

  // Calculate progress as percentage of primary assets
  const progress =
    totalCount > 0 ? Math.round((loadedCount / totalCount) * 100) : 0;

  // Load secondary assets in background (after page is visible)
  const loadSecondaryAssets = useCallback(() => {
    if (hasStartedBackground.current) return;
    hasStartedBackground.current = true;

    const secondaryAssets = allAssets.current.slice(
      primaryAssets.current.length,
    );
    console.log(
      `🔄 [Background] Starting to load ${secondaryAssets.length} secondary assets...`,
    );
    secondaryAssets.forEach((src) => {
      preloadAsset(src); // Fire and forget - just cache them
    });
  }, []);

  useEffect(() => {
    let timeoutId;
    let isMounted = true;

    const loadPrimaryAssets = async () => {
      const assets = primaryAssets.current;
      let loaded = 0;

      // Start timeout - show page after this regardless of progress
      timeoutId = setTimeout(() => {
        if (isMounted && !isReady) {
          setIsReady(true);
          loadSecondaryAssets();
        }
      }, PRELOAD_CONFIG.timeout);

      // Load all primary assets in parallel
      const promises = assets.map(async (src) => {
        const result = await preloadAsset(src);
        if (isMounted) {
          loaded++;
          setLoadedCount(loaded);
        }
        return result;
      });

      await Promise.all(promises);

      // All primary assets loaded before timeout
      if (isMounted) {
        clearTimeout(timeoutId);
        setIsReady(true);
        loadSecondaryAssets();
      }
    };

    loadPrimaryAssets();

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadSecondaryAssets]);

  return {
    isReady,
    progress,
    loadedCount,
    totalCount,
  };
};

export default useAssetPreloader;
